/**
 * What the role asks for, as read off the job description.
 *
 * WHY THIS WAS REWRITTEN. The section had five near-identical blocks that each
 * rendered the first two items and hid the rest behind a "more" dropdown. The
 * dropdown existed because the cards were pinned to `height: 134px` and could
 * not grow -- so a job with five required skills showed two, and a reader had
 * to open a popover to learn what the role actually requires. That is a
 * strange thing to hide on the page whose entire subject is the requirements.
 *
 * The same data also rendered two different ways depending on how much of it
 * there was: fewer than three items became pills, three or more became a plain
 * text list plus a dropdown. Same field, same meaning, two appearances.
 *
 * Everything now renders as chips, all of it, and the cards size to their
 * content. Required skills use the accent treatment the candidate scorecard
 * gives a met requirement, so the two surfaces read as one product rather than
 * two.
 *
 * The edit affordance is gone at Nachi's request, so this component no longer
 * edits anything -- hence the rename from skills-education-edit. The modal it
 * opened is deleted; `skills-education-step`, which that modal shared with the
 * create-job wizard, is untouched and still used there.
 */
import { memo, ReactNode } from 'react';

import { ScheduleOutlined } from '@ant-design/icons';

import styles from './index.module.css';
import { academicDegreeMapping } from '../../../../components/common/education-list-maker';
import { Job } from '../../../../fsm/jobs';

const emptyStateImg = `${process.env.PUBLIC_URL}/images/emty-state.svg`;

interface Props {
  data: Job;
}

/** A card of chips, or an explicit absence. Never a truncated list. */
const ChipCard = ({
  title,
  items,
  emphasis,
  empty,
}: {
  title: string;
  items: string[];
  emphasis?: boolean;
  empty: string;
}) => (
  <div className={styles.card}>
    <div className={styles.cardHead}>
      <span className={styles.cardTitle}>{title}</span>
    </div>
    {items.length ? (
      <div className={styles.chips}>
        {items.map((item) => (
          <span
            key={item}
            className={emphasis ? styles.chipRequired : styles.chip}
          >
            {item}
          </span>
        ))}
      </div>
    ) : (
      <span className={styles.none}>{empty}</span>
    )}
  </div>
);

const FactCard = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div className={styles.card}>
    <div className={styles.cardHead}>
      <span className={styles.cardTitle}>{title}</span>
    </div>
    <div className={styles.factRow}>{children}</div>
  </div>
);

const formatExperience = (min?: number, max?: number) => {
  if (!min && !max) return '';
  if (!max || max === 0) return `${min}+`;
  if (!min || min === 0) return `${max}+`;
  return `${min}-${max}`;
};

const SkillsExperience = ({ data }: Props) => {
  const totalExperience = formatExperience(
    data?.minTotalExperience,
    data?.maxTotalExperience,
  );
  const industryExperience = formatExperience(
    data?.minIndustryExperience,
    data?.maxIndustryExperience,
  );

  const certifications = data.certification?.certifications ?? [];
  const licensures = data.certification?.licensures ?? [];

  // "Bachelor's in Computer Science", or just the major when the degree is
  // absent or is a value the mapping does not know. The previous version
  // concatenated a literal "in ${major}" onto a possibly-empty degree, so an
  // unmapped academicDegree rendered as a dangling "in Computer Science".
  const degrees = data.educationList
    .map((item) => {
      const degree =
        academicDegreeMapping[
          item.academicDegree as keyof typeof academicDegreeMapping
        ];
      if (degree && item.major) return `${degree} in ${item.major}`;
      return degree || item.major || '';
    })
    .filter(Boolean);

  const hasNothing =
    !data.requiredSkills.length &&
    !data.preferredSkills.length &&
    !totalExperience &&
    !industryExperience &&
    !data.hasManagementExperience &&
    !degrees.length &&
    !certifications.length &&
    !licensures.length;

  return (
    <div
      className={hasNothing ? styles.body : `${styles.body} ${styles.active}`}
    >
      <div className={styles.header}>
        <span className={styles.headerIcon}>
          <ScheduleOutlined />
        </span>
        <h2 className={styles.title}>Skills &amp; experience</h2>
      </div>
      <p className={styles.subtitle}>
        What the role asks for, read from the job description. Not a measure of
        any candidate — the fit scores elsewhere on this page are.
      </p>
      <hr className={styles.rule} />

      {hasNothing ? (
        <div className={styles.emptyContainer}>
          <img src={emptyStateImg} alt="" />
          <span className={styles.none}>
            No skills or experience have been identified for this role yet.
          </span>
        </div>
      ) : (
        <div className={styles.grid}>
          <ChipCard
            title="Required skills"
            items={data.requiredSkills}
            emphasis
            empty="None identified"
          />
          <ChipCard
            title="Preferred skills"
            items={data.preferredSkills}
            empty="None identified"
          />
          <FactCard title="Experience">
            {totalExperience && (
              <span className={styles.fact}>
                <span className={styles.factLabel}>Total </span>
                {totalExperience} years
              </span>
            )}
            {industryExperience && (
              <span className={styles.fact}>
                <span className={styles.factLabel}>Industry </span>
                {industryExperience} years
              </span>
            )}
            {data.hasManagementExperience && (
              <span className={styles.fact}>
                Management experience expected
              </span>
            )}
            {!totalExperience &&
              !industryExperience &&
              !data.hasManagementExperience && (
                <span className={styles.none}>None identified</span>
              )}
          </FactCard>
          <FactCard title="Education">
            {degrees.length ? (
              degrees.map((d) => (
                <span className={styles.fact} key={d}>
                  {d}
                </span>
              ))
            ) : (
              <span className={styles.none}>None identified</span>
            )}
          </FactCard>
          <ChipCard
            title="Certifications"
            items={certifications}
            empty="None identified"
          />
          <ChipCard
            title="Licensures"
            items={licensures}
            empty="None identified"
          />
        </div>
      )}
    </div>
  );
};

export default memo(SkillsExperience);
